<template>
	<Card>
		<h2>{{ eventName }} leaderboards</h2>
		<p v-if="is('error')">Error loading this leaderboard.</p>
		<p v-else-if="is('loading')">Loading...</p>
		<template v-else>
			<FieldSelect name="leaderboardRange" :options="leaderboardOptions" v-model="selected" />
			<table v-if="selectedHasScores" class="scores">
				<tbody>
					<LeaderboardRow
						v-for="score in selectedLeaderboard.scores"
						:key="score._id"
						v-bind="score"
					>
						<Action title="Ban" @click="targetUser(score.user)" :disabled="is('loading')"
							><IconBan
						/></Action>
					</LeaderboardRow>
				</tbody>
			</table>
			<p v-else>This leaderboard is currently empty.</p>
		</template>
		<!-- Ban confirmation modal -->
		<Modal v-if="userToBan" @close="hideBanModal" title="Permanently ban user?">
			<p>
				This will remove <strong>{{ userToBan.nickname }}</strong> from all competition and global
				leaderboards. This action cannot be undone.
			</p>
			<div class="modal-buttons">
				<button class="primary" type="button" @click="banTargetedUser">Ban user</button>
				<button class="secondary" type="button" @click="hideBanModal">Cancel</button>
			</div>
		</Modal>
	</Card>
</template>

<script>
import { GLOBAL_EVENT } from 'recycle-race-common/lib/constants'
import { LeaderboardTitle } from 'recycle-race-common/lib/labels'
import { UserProvider, LeaderboardsProvider } from '@/lib/dataProvider'
import Card from '@/components/Card'
import FieldSelect from '@/components/FieldSelect'
import LeaderboardRow from '@/components/LeaderboardRow'
import Action from '@/components/Action'
import Modal from '@/components/Modal'
import IconBan from 'mdi-vue/AccountRemove'
import state from '@/mixins/state'
import toast from '@/mixins/toast'

export default {
	name: 'ModerationForm',
	props: {
		event: {
			type: Object,
			required: true,
		},
	},
	data() {
		return {
			selected: null,
			leaderboards: [],
			userToBan: null,
		}
	},
	computed: {
		eventName() {
			if (this.event._id === GLOBAL_EVENT) {
				return 'Global'
			} else {
				return this.event.name
			}
		},
		leaderboardOptions() {
			return this.leaderboards.map(lb => {
				const value = lb.range
				const name = lb.name || LeaderboardTitle[value]
				return {
					label: `${name} (${lb.scores.length})`,
					value,
				}
			})
		},
		selectedLeaderboard() {
			return this.leaderboards.find(lb => lb.range === this.selected)
		},
		selectedHasScores() {
			return this.selectedLeaderboard && this.selectedLeaderboard.scores.length
		},
	},
	methods: {
		async getLeaderboards() {
			this.setState('loading')
			try {
				this.leaderboards = await LeaderboardsProvider.get(this.event._id)
				this.setState('idle')
			} catch (err) {
				this.setState('error')
			}
		},
		targetUser(user) {
			this.userToBan = user
		},
		async banTargetedUser() {
			this.setState('loading')

			// Mark user as flagged
			try {
				// Get ID of user to ban, then hide ban modal
				const id = this.userToBan._id
				this.hideBanModal()

				// Flag the user
				const user = await UserProvider.update(id, { flagged: true })
				this.toast(`User *${user.nickname}* was blocked succesfully.`)

				// Emit ban to parent to force leaderboard refresh
				this.$emit('ban', user)
			} catch (err) {
				this.setState('idle')
				this.toastError('Failed to ban the user.')
			}
		},
		hideBanModal() {
			this.userToBan = null
		},
	},
	created() {
		this.selected = this.event.leaderboards[0].range
		this.getLeaderboards()
	},
	mixins: [state(), toast],
	components: {
		Card,
		FieldSelect,
		LeaderboardRow,
		Action,
		Modal,
		IconBan,
	},
}
</script>

<style lang="scss" scoped>
@import '~scss/components';
@import '~scss/layout';
@import '~scss/mq';

.scores {
	@extend %table;
}

.modal-buttons {
	@include modal-buttons;
}
</style>
